import { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSaleListHooks } from './hooks/useSaleListHooks';
import Loading from 'components/Loading/Loading';
import SaleProductSection from './SaleProductSection';
import SaleDetailSection from './SaleDetailSection';
import { SALE_DETAIL_PATH } from 'constants/path';
import { HEADER_HEIGHT } from 'constants/style';

const Container = styled.div<{ $windowHeight: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 32px;

  width: 100%;
  height: 100%;

  max-width: 1280px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  position: sticky;
  top: 0;
  padding: 24px 20px;
  width: 100%;
  background-color: #fff;
  border-bottom: 1px solid #eaebec;
  z-index: 5;
`;

const ContentBox = styled.div`
  display: flex;
  align-items: flex-start;

  position: relative;

  width: 100%;
  max-width: 1200px;
  height: 100%;

  padding: 0px;

  border-left: 1px solid #eaebec;
  border-right: 1px solid #eaebec;

  background: #fff;
`;

const SaleListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>('null');

  const selectedOrderId = localStorage.getItem('orderId'); // 예약 페이지에서 넘어온 값
  const selectedScheduleIds = localStorage.getItem('scheduleIds'); // 예약 페이지에서 넘어온 값

  // 상세조회 핸들러
  const handleSelectSaleId = (id: string) => {
    setSelectedSaleId(id);

    if (isMobile) {
      if (isMobile) {
        return navigate(`${SALE_DETAIL_PATH}/${id}`);
      }
    }
  };

  const useSaleListHooksData = useSaleListHooks();

  const { width, height, ref } = useResizeDetector();

  const isMobile = width < 768;

  const [windowHeight, setWindowHeight] = useState(
    document.documentElement.clientHeight - HEADER_HEIGHT,
  );

  useEffect(() => {
    if (selectedOrderId) {
      setSelectedSaleId(selectedOrderId);
    }
  }, [selectedOrderId]);

  useEffect(() => {
    if (selectedScheduleIds) {
      useSaleListHooksData.handleSetOrderFilterInput('filter', {
        ...useSaleListHooksData.orderFilterInput.filter,
        scheduleIds: selectedScheduleIds,
      });
    }
  }, [selectedScheduleIds]);

  useEffect(() => {
    const handleResize = () => {
      const viewportHeight = document.documentElement.clientHeight;

      setWindowHeight(viewportHeight - 81);
    };

    // `resize` 이벤트 리스너 추가
    window.addEventListener('resize', debounce(handleResize, 500));

    // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Container ref={ref} $windowHeight={windowHeight}>
      {useSaleListHooksData.orderItems ? (
        <ContentBox>
          <SaleProductSection
            isMobile={isMobile}
            selectedSaleId={selectedSaleId}
            selectedOrderId={selectedOrderId}
            selectedScheduleIds={selectedScheduleIds}
            handleSelectSaleId={handleSelectSaleId}
            {...useSaleListHooksData}
          />

          {isMobile ? (
            <></>
          ) : (
            <SaleDetailSection
              isMobile={isMobile}
              orderItems={useSaleListHooksData.orderItems}
              selectedSaleId={selectedSaleId}
            />
          )}
        </ContentBox>
      ) : (
        <Loading />
      )}
    </Container>
  );
};

export default SaleListPage;
